import { useState, useEffect, useContext } from 'react';
import { Box, Grid } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttDialog,
    SttTable,
    SttFormCheck,
    SttFormSearch,
    SttFormTime,
} from '../../../sporttia/all';
import { updateElementInArray, formatPriceByLocale } from '../../../lib/utils';

type Props = {
    open?: boolean;
    facility: any;
    date: string;
    piece: any;
    onClose: () => void;
};

export default function SeatingDialog({
    open = false,
    facility,
    date,
    piece,
    onClose,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [occupants, setOccupants] = useState<any>();
    const [name, setName] = useState<string>('');
    const [time, setTime] = useState<string>();

    /**
     * Piece changes
     */
    useEffect(() => {
        if (piece && piece.ini) {
            setTime(format(parseISO(piece.ini), 'H:mm'));
        }
    }, [piece]);

    /**
     * Load occupants
     */
    const loadOccupants = (p = { page: 1, rows: 6 }) => {
        cxt?.api('GET', `/facilities/${facility.id}/bookings/occupants`, {
            params: {
                date: format(parseISO(date), 'yyyy-MM-dd'),
                name,
                time,
                page: p.page,
                rows: p.rows,
            },
            success: (r: any) => {
                const filteredOccupants = r.rows.filter((occupant: any) => {
                    const fullName =
                        occupant.user?.mship?.fullName ||
                        occupant.user?.fullName ||
                        '';
                    return fullName.toLowerCase().includes(name.toLowerCase());
                });
                setOccupants({
                    ...r,
                    rows: filteredOccupants,
                });
            },
        });
    };

    /**
     * Toggle confirmed
     */
    const toggleConfirmed = (occupant: any) => {
        cxt?.api('PUT', `/bookings/occupants/${occupant.id}`, {
            params: {
                confirmed: !occupant.confirmed,
            },
            success: () => {
                setOccupants({
                    ...occupants,
                    rows: updateElementInArray(occupants.rows, {
                        ...occupant,
                        confirmed: !occupant.confirmed,
                    }),
                });
            },
        });
    };

    /**
     * Init
     */
    useEffect(() => {
        if (facility && facility.id && time) loadOccupants();
    }, [facility, time]);

    // -----| Render |-----
    return (
        <SttDialog
            fullWidth
            maxWidth="lg"
            title={t('Seating')}
            open={open}
            onClose={onClose}
            content={
                <>
                    <Box mb={3}>
                        <Grid container spacing={3}>
                            <SttFormSearch
                                {...({
                                    grid: true,
                                    md: 5,
                                    sm: true,
                                    xs: true,
                                    defVal: name,
                                    onChange: ({
                                        value,
                                    }: {
                                        name: string;
                                        value: unknown;
                                    }) => setName(value as string),
                                    onClickSearch: loadOccupants,
                                } as any)}
                            />

                            <SttFormTime
                                {...({
                                    grid: true,
                                    md: 3,
                                    sm: true,
                                    xs: true,
                                    defVal: time,
                                    onChange: ({
                                        value,
                                    }: {
                                        name: string;
                                        value: unknown;
                                    }) => setTime(value as string),
                                } as any)}
                            />
                        </Grid>
                    </Box>

                    <SttTable
                        data={occupants}
                        perPage={6}
                        columns={
                            [
                                {
                                    title: t('Name'),
                                    field: 'userName',
                                    align: 'left',
                                    value: (row: any) => {
                                        const fullName =
                                            row.user?.mship?.fullName ||
                                            row.user?.fullName ||
                                            'N/A';
                                        return fullName;
                                    },
                                },
                                {
                                    title: t('Creator'),
                                    field: 'creator',
                                    align: 'left',
                                    value: (row: any) =>
                                        row.booking.creator &&
                                        row.booking.creator.fullName,
                                },
                                {
                                    title: t('Period'),
                                    type: 'periodTime',
                                    value: (row: any) => ({
                                        ini: row.booking.ini,
                                        end: row.booking.end,
                                    }),
                                },
                                {
                                    title: `${t('Rate')} / ${t('Bono')}`,
                                    value: (row: any) =>
                                        row.rate && row.rate.length > 0
                                            ? row.rate
                                                  .map(
                                                      (price: any) =>
                                                          `${
                                                              price.name
                                                          } [${formatPriceByLocale(
                                                              price.price,
                                                              cxt?.sc?.city
                                                                  ?.country
                                                                  ?.currency,
                                                          )}]`,
                                                  )
                                                  .join(' - ')
                                            : row.bonoMov &&
                                              row.bonoMov.boleto.bono.name,
                                },
                                {
                                    title: t('Confirmed'),
                                    value: (row: any) => (
                                        <SttFormCheck
                                            color="primary"
                                            checked={row.confirmed}
                                            onChange={() =>
                                                toggleConfirmed(row)
                                            }
                                        />
                                    ),
                                },
                            ] as any
                        }
                        onFetch={loadOccupants}
                    />
                </>
            }
            buttons={
                [
                    {
                        type: 'close',
                        onClick: onClose,
                    },
                ] as any
            }
        />
    );
}
